import { ChangeDetectionStrategy, Component, Input, OnInit, Inject, ViewChild, TemplateRef, ChangeDetectorRef, AfterViewInit, OnChanges, SimpleChanges, ViewEncapsulation } from '@angular/core';
import GridComponentTemplate from './grid.component.html';
import { GridConfig, GridFilterPrefixRule } from './models/grid-config.model';
import { FilterMetadata, FilterService, MenuItem } from 'primeng/api';
import {TranslateService, _} from "@ngx-translate/core";
import { GridColumn } from './models/grid-column.model';
import { Table, TableFilterEvent } from 'primeng/table';
import moment from 'moment';
import { TooltipModule } from 'primeng/tooltip';

import { LogService } from '../../../core/services/log.service';

@Component({
  selector: 'd-grid',
  template: GridComponentTemplate,
  styles: [`
    .column-filter-active :is(.p-column-filter-menu-button, .p-column-filter-menu-button-open, .p-datatable-column-filter-button) {
      color: #2563eb !important;
    }
    .column-filter-active .p-datatable-column-filter-button .p-button,
    .column-filter-active .p-datatable-column-filter-button button {
      color: #2563eb !important;
    }
    .column-filter-active svg path[fill="currentColor"] {
      stroke: currentColor;
      stroke-width: 3px;
      stroke-linejoin: round;
      paint-order: stroke fill;
    }
    .column-filter-active .p-column-filter-menu-button .pi-filter::before {
      content: '\e9ac';
    }
    .skin-3 .p-datatable .p-datatable-tbody > tr:hover > td {
      background-color: #faecc7 !important;
    }
  `],
  encapsulation: ViewEncapsulation.None,
  // #2912: OnPush — todos los callers actuales (guest-list, access-control-list,
  // identification-types-list, languages-list) reasignan el array `data` por
  // inmutabilidad (filter/spread). Si en el futuro un caller hace push/splice
  // sobre el array original, hay que clonarlo antes para que el cambio se vea.
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GridComponent implements OnInit, AfterViewInit, OnChanges {
  @ViewChild('grid') table: Table;
  @Input() headerAdditional: TemplateRef<any>;
  @Input() headerInfo: TemplateRef<any>;
  @Input() headerBottom: TemplateRef<any>;
  @Input() tableHeaderAdditional: TemplateRef<any>;  

  private _config: GridConfig;
  @Input() data: any[];
  selectedRows: any[] = [];
  selectedActionsMenuitems: MenuItem[] = [];
  selectedColumns: GridColumn[] = [];
  searchValue: string = '';
  selectedSort: string | null = null;
  timezoneOffset: string = '';
  groupRowsBy = null;
  title: string = '';
  toggeable: boolean = true;
  filteredCount: number = 0;
  activeFilterChips: Array<{ field: string; label: string; valueLabel: string }> = [];
  expandedGroupRows: Record<string, boolean> = {};
  private previousGroupRowsBy: string | null = null;

  private static customFiltersRegistered = false;

  constructor(@Inject(TranslateService) private translate: TranslateService,
              @Inject(LogService) private log: LogService,
              @Inject(ChangeDetectorRef) private changeDetector: ChangeDetectorRef,
              @Inject('datasetsService') private datasetsService: any,
              @Inject(FilterService) private filterService: FilterService) {
    this.ensureCustomFiltersRegistered();
  }

  ngOnInit() {}

  // #2912: trackBy para los *ngFor del template (columnas, chips, acciones).
  // Sin trackBy y bajo OnPush, los nodos del DOM se recrean cada vez que la
  // referencia del array cambia; con trackBy se reutilizan los nodos cuyo key
  // no haya cambiado.
  trackByColumnName(_index: number, col: GridColumn): string { return col.name; }
  trackByChipField(_index: number, chip: { field: string }): string { return chip.field; }
  trackByActionLabel(_index: number, action: any): string { return action?.label ?? String(_index); }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.data) {
      this.syncFilteredCount();
      if (this._config?.groupRowsBy && !this._config?.collapseGroupsByDefault) {
        this.expandAllGroups();
      }
    }
  }

  // #2909: la lógica de detección de groupRowsBy se mueve al setter `config` para que sólo
  // se ejecute cuando el padre cambia el Input, no en cada ciclo de change detection.
  // El ngDoCheck anterior recalculaba expandAllGroups() en cada tick, lo que con listas
  // grandes era un coste enorme y disparaba un re-render constante.

  @Input() set config(value: GridConfig) {
    if(value.timezone !== undefined && value.timezone !== null) {
      this.timezoneOffset = moment.tz(value.timezone).format('Z');
    }
    // #2909: detectar cambio de groupRowsBy aquí (antes en ngDoCheck)
    const newGroupRowsBy = value?.groupRowsBy || null;
    if (newGroupRowsBy !== this.previousGroupRowsBy) {
      if (value?.collapseGroupsByDefault) {
        this.expandedGroupRows = {};
      } else if (newGroupRowsBy) {
        // expandAllGroups() depende de this._config y this.data ya asignados;
        // se ejecuta tras el set de _config más abajo y al final del setter.
      } else {
        this.expandedGroupRows = {};
      }
      this.previousGroupRowsBy = newGroupRowsBy;
    }
    this._config = value;
    this.toggeable = this._config.toggleablePanel !== undefined ? this._config.toggleablePanel : true;
    this.selectedColumns = this._config.columns;
    for (const column of this._config.hiddenColumns || []) {
      const index = this.selectedColumns.findIndex(c => c.name === column);
      if (index !== -1) {
        this.selectedColumns.splice(index, 1);
      }
    }
    if(this._config.selectionActionItems) {
      this.selectedActionsMenuitems = this._config.selectionActionItems.map(action => ({
        label: this.translate.instant(_(action.label || '')),
        icon: action.icon,
        command: () => action.command(this.selectedRows)
      }));
    }
    this.groupRowsBy = this._config.groupRowsBy;
    // #2909: si el grupo está activo y no se colapsa por defecto, expandir tras tener
    // _config y data sincronizados (sustituye al ngDoCheck que ejecutaba esto en cada tick).
    if (newGroupRowsBy && !this._config?.collapseGroupsByDefault) {
      this.expandAllGroups();
    }
    if(this._config.columns) {
      this.changeDetector.detectChanges();
    }
  }

  ngAfterViewInit(): void {
    this.normalizeFiltersAfterStateRestore();
  }

  onFilter(event: TableFilterEvent) {
    if(event.filters && event.filters.global) {
      if (Array.isArray(event.filters.global)) {
        this.searchValue = (event.filters.global as FilterMetadata[])[0]?.value || '';
      } else {
        this.searchValue = event.filters.global.value || '';
      }
    }

    const filteredValue = (event as any)?.filteredValue as any[] | undefined | null;
    if (Array.isArray(filteredValue)) {
      this.filteredCount = filteredValue.length;
    } else {
      this.syncFilteredCount();
    }

    if (this._config?.filterPrefixRules && this._config.filterPrefixRules.length > 0) {
      this.normalizeFiltersAfterStateRestore();
    }
    this.syncActiveFilterChips();
  }

  getHeaderDisplayName(displayName: string) {
    return this.translate.instant(displayName);
  }

  get config(): GridConfig {
    return this._config;
  }

  clear(table: Table) {
    this.searchValue = '';
    this.clearPersistedTableState();

    table.clear();
    this.syncFilteredCount();
    this.syncActiveFilterChips();

    if (this._config.clearCallback) {
      this._config.clearCallback();
    }
  }

  get totalCount(): number {
    return this.data ? this.data.length : 0;
  }

  getFooterColspan(): number {
    let count = this.selectedColumns?.length || 0;
    if (this._config?.enableRowExpand) {
      count += 1;
    }
    if (this._config?.enableSelection) {
      count += 1;
    }
    if (this._config?.actions) {
      count += 1;
    }
    return Math.max(count, 1);
  }

  private syncFilteredCount(): void {
    const filtered = this.table?.filteredValue as any[] | null | undefined;
    if (Array.isArray(filtered)) {
      this.filteredCount = filtered.length;
    } else {
      this.filteredCount = this.totalCount;
    }
  }

  private clearPersistedTableState(): void {
    if (typeof window === 'undefined' || !this._config?.stateKey) {
      return;
    }

    const storageType = this._config.stateStorage || 'local';
    if (storageType === 'local' && window.localStorage) {
      window.localStorage.removeItem(this._config.stateKey);
    }

    if (storageType === 'session' && window.sessionStorage) {
      window.sessionStorage.removeItem(this._config.stateKey);
    }
  }

  export() {
    // Copia: el modal de exportación pone FILTERED_IDS a null si no se aplica el filtro
    const options = this._config.exportOptions && { ...this._config.exportOptions };

    // Si la tabla tiene filtros de columna o búsqueda activos, los IDs son los de las filas visibles
    const filtered = this.table?.filteredValue as any[] | null | undefined;
    if (options && 'FILTERED_IDS' in options && Array.isArray(filtered)) {
      options.FILTERED_IDS = filtered.map(row => row[this._config.dataKey]);
    }

    this.datasetsService.exportByType(this._config.exportType, options);
  }

  filterGlobal(value: string): void {
    this.searchValue = value || '';
    this.table?.filterGlobal(this.searchValue, 'contains');
  }

  handleStateRestore(event: any) {
    if (this._config?.collapseGroupsByDefault && this._config?.groupRowsBy) {
      this.expandedGroupRows = {};
      if (this.table) {
        (this.table as any).expandedRowKeys = {};
      }
    }
    this.normalizeFiltersAfterStateRestore();
    this.syncActiveFilterChips();
    if (this._config?.stateRestoredCallback) {
      this._config.stateRestoredCallback(event);
    }
  }

  onRowToggle(event: any): void {
    this.expandedGroupRows = event?.data || {};
  }

  toggleGroup(rowData: any): void {
    const key = this.getGroupKey(rowData);
    if (key == null) {
      return;
    }
    if (this.expandedGroupRows[key]) {
      delete this.expandedGroupRows[key];
    } else {
      this.expandedGroupRows[key] = true;
    }
    this.expandedGroupRows = { ...this.expandedGroupRows };
  }

  isGroupExpanded(rowData: any): boolean {
    const key = this.getGroupKey(rowData);
    return key != null && !!this.expandedGroupRows[key];
  }

  private expandAllGroups(): void {
    const expanded: Record<string, boolean> = {};
    if (this.data && this._config?.groupRowsBy) {
      for (const row of this.data) {
        const key = this.getGroupKey(row);
        if (key != null) {
          expanded[key] = true;
        }
      }
    }
    this.expandedGroupRows = expanded;
  }

  private getGroupKey(rowData: any): string | null {
    if (!this._config?.groupRowsBy || !rowData) {
      return null;
    }
    const parts = this._config.groupRowsBy.split('.');
    let value = rowData;
    for (const part of parts) {
      if (value == null) {
        return null;
      }
      value = value[part];
    }
    return value != null ? String(value) : null;
  }

  onRowClick(event: MouseEvent, rowData: any): void {
    if (!this._config?.rowClickCallback) {
      return;
    }

    const target = event.target as HTMLElement | null;
    if (!target) {
      this._config.rowClickCallback(rowData);
      return;
    }

    const interactiveSelector = [
      'button',
      'a',
      'input',
      'textarea',
      'select',
      '[role="button"]',
      '.p-button',
      '.p-checkbox',
      '.p-dropdown',
      '.p-multiselect',
      '.p-column-filter-menu-button',
      '.p-column-filter-clear-button',
      '.p-column-filter-overlay',
      '.p-datatable-column-filter-button',
      '.p-datatable-column-filter-clear-button',
      '.p-table-header-checkbox',
      '.p-table-checkbox'
    ].join(',');

    if (target.closest(interactiveSelector)) {
      return;
    }

    this._config.rowClickCallback(rowData);
  }

  getRowClasses(rowData: any): string | string[] | Record<string, boolean> | undefined {
    if (!this._config?.rowClassCallback) {
      return undefined;
    }

    return this._config.rowClassCallback(rowData);
  }

  isFieldFiltered(field: string): boolean {
    if (!this.table?.filters || !field) {
      return false;
    }

    const metadata = (this.table.filters as Record<string, any>)[field];
    return this.filterMetadataHasValue(metadata);
  }

  removeFilter(field: string): void {
    if (!this.table || !field) {
      return;
    }

    this.table.filter(null, field, 'contains');
    this.syncFilteredCount();
    this.syncActiveFilterChips();
  }

  private normalizeFiltersAfterStateRestore(attempt: number = 0): void {
    const prefixes = this._config?.filterPrefixRules;
    if (!prefixes || prefixes.length === 0) {
      return;
    }

    if (!this.table) {
      if (attempt >= 10) {
        return;
      }
      setTimeout(() => this.normalizeFiltersAfterStateRestore(attempt + 1), 50);
      return;
    }

    const filters = this.table.filters as Record<string, any>;
    if (!filters) {
      if (attempt >= 10) {
        return;
      }
      setTimeout(() => this.normalizeFiltersAfterStateRestore(attempt + 1), 50);
      return;
    }

    const allColumnNames: string[] = [];
    (this._config?.columns || []).forEach(column => {
      if (column?.name) {
        allColumnNames.push(column.name);
      }
    });
    (this.selectedColumns || []).forEach(column => {
      if (column?.name) {
        allColumnNames.push(column.name);
      }
    });
    const availableColumns = new Set(allColumnNames);

    const mutated = this.normalizeFilterDictionary(filters, prefixes, availableColumns);

    if (mutated) {
      if (typeof (this.table as any)._filter === 'function') {
        (this.table as any)._filter();
      }
      if (typeof this.table.saveState === 'function') {
        this.table.saveState();
      }
    }
  }

  private normalizeFilterDictionary(filters: Record<string, any>, rules: GridFilterPrefixRule[], availableColumns: Set<string>): boolean {
    let mutated = false;

    Object.entries(filters || {}).forEach(([field, metadata]) => {
      const rule = this.resolveRuleForField(field, rules);
      if (!rule) {
        return;
      }

      if (this.shouldDropField(field, availableColumns, rule)) {
        delete filters[field];
        mutated = true;
        return;
      }

      if (!metadata) {
        return;
      }

      if (Array.isArray(metadata)) {
        metadata.forEach(entry => {
          mutated = this.normalizeFilterEntry(entry, rule) || mutated;
        });
      } else {
        mutated = this.normalizeFilterEntry(metadata, rule) || mutated;
      }
    });

    return mutated;
  }

  private resolveRuleForField(field: string, rules: GridFilterPrefixRule[]): GridFilterPrefixRule | undefined {
    return rules.find(rule => field?.startsWith(rule.prefix));
  }

  private shouldDropField(field: string, availableColumns: Set<string>, rule: GridFilterPrefixRule): boolean {
    if (rule.dropIfMissingColumn === false) {
      return false;
    }

    return availableColumns.size > 0 && !availableColumns.has(field);
  }

  private normalizeFilterEntry(entry: any, rule: GridFilterPrefixRule): boolean {
    if (!entry) {
      return false;
    }

    let mutated = false;
    const forceArray = rule.forceArrayValue !== false;
    const desiredMatchMode = rule.matchMode || 'stringIn';

    if (forceArray && entry.value != null && !Array.isArray(entry.value)) {
      entry.value = [entry.value];
      mutated = true;
    }

    if (entry.matchMode !== desiredMatchMode) {
      entry.matchMode = desiredMatchMode;
      mutated = true;
    }

    return mutated;
  }

  private ensureCustomFiltersRegistered(): void {
    if (GridComponent.customFiltersRegistered) {
      return;
    }

    this.filterService.register('stringIn', (value: string | null, filter: FilterMetadata | string[] | string | null): boolean => {
      const selectedValues = this.normalizeStringFilterValues(filter);

      if (!selectedValues || selectedValues.length === 0) {
        return true;
      }

      if (value == null) {
        return selectedValues.some(v => v == null);
      }

      return selectedValues.includes(value);
    });

    GridComponent.customFiltersRegistered = true;
  }

  private normalizeStringFilterValues(filter: FilterMetadata | string[] | string | null | undefined): string[] | null {
    if (filter == null) {
      return null;
    }

    if (Array.isArray(filter)) {
      return filter as string[];
    }

    if (typeof filter === 'string') {
      return [filter];
    }

    if (filter && typeof filter === 'object' && Object.prototype.hasOwnProperty.call(filter, 'value')) {
      const metadataValue = filter.value;
      if (metadataValue == null) {
        return null;
      }
      if (Array.isArray(metadataValue)) {
        return metadataValue as string[];
      }
      return [metadataValue as string];
    }

    return null;
  }

  private syncActiveFilterChips(): void {
    if (!this.table?.filters) {
      this.activeFilterChips = [];
      return;
    }

    const filters = this.table.filters as Record<string, any>;
    const chips: Array<{ field: string; label: string; valueLabel: string }> = [];

    Object.entries(filters).forEach(([field, metadata]) => {
      if (field === 'global' || !this.filterMetadataHasValue(metadata)) {
        return;
      }

      const valueLabel = this.getMetadataDisplayValue(metadata, field);
      chips.push({
        field,
        label: this.getColumnDisplayName(field),
        valueLabel
      });
    });

    this.activeFilterChips = chips;
  }

  private getColumnDisplayName(field: string): string {
    const column = (this._config?.columns || []).find(c => c.name === field);
    if (column?.header?.displayName) {
      return this.translate.instant(column.header.displayName);
    }
    return field;
  }

  private filterMetadataHasValue(metadata: any): boolean {
    if (metadata == null) {
      return false;
    }

    if (Array.isArray(metadata)) {
      return metadata.some(entry => this.filterMetadataHasValue(entry));
    }

    if (typeof metadata === 'object' && Object.prototype.hasOwnProperty.call(metadata, 'value')) {
      const value = metadata.value;
      if (value == null) {
        return false;
      }
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      if (typeof value === 'string') {
        return value.trim().length > 0;
      }
      if (typeof value === 'object') {
        return Object.values(value).some(v => v != null && `${v}`.trim() !== '');
      }
      return true;
    }

    return false;
  }

  private getMetadataDisplayValue(metadata: any, field?: string): string {
    if (metadata == null) {
      return '';
    }

    const normalized = Array.isArray(metadata) ? metadata[0] : metadata;
    if (!normalized || typeof normalized !== 'object' || !Object.prototype.hasOwnProperty.call(normalized, 'value')) {
      return '';
    }

    const value = normalized.value;
    if (value == null) {
      return '';
    }

    if (Array.isArray(value)) {
      return value.map(entry => this.getFieldValueLabel(field, entry)).join(', ');
    }

    if (typeof value === 'object') {
      const pieces = Object.values(value).filter(v => v != null && `${v}`.trim() !== '');
      return pieces.join(' · ');
    }

    return this.getFieldValueLabel(field, value);
  }

  private getFieldValueLabel(field: string | undefined, rawValue: any): string {
    if (rawValue == null) {
      return '';
    }

    const column = (this._config?.columns || []).find(c => c.name === field);
    const values = column?.filter?.values;

    if (Array.isArray(values) && values.length > 0) {
      const match = values.find((option: any) => option?.id === rawValue || `${option?.id}` === `${rawValue}`);
      if (match?.name != null && `${match.name}`.trim() !== '') {
        return this.translate.instant(`${match.name}`);
      }
    }

    return this.translate.instant(`${rawValue}`);
  }
}
